/** One archetype in detail: what the recipe builds, and which tenants have it.
 *
 *  The tenant table is the reason this exists. `stale` — a tenant still running
 *  an older version of the wording — is not visible anywhere else, because a
 *  stale notification looks entirely normal from every other angle.
 */
import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import * as cannedApi from '@/api/cannedNotifications';
import { MANAGED_LABELS, type CannedNotificationListItem } from '@/api/cannedNotifications';
import { formatRelativeTime } from '@/utils/format';

export function CannedNotificationPanel({ row }: { row: CannedNotificationListItem }) {
  const query = useQuery({
    queryKey: ['canned_notifications', 'detail', row.id],
    queryFn: () => cannedApi.retrieve(row.id),
  });
  const detail = query.data;

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        {row.description}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Chip size="small" variant="outlined" label={row.event} sx={{ fontFamily: 'monospace' }} />
        {row.block_lists.map((c) => (
          <Chip key={c} size="small" variant="outlined" label={`list: ${c}`} />
        ))}
        {row.rulesets.map((c) => (
          <Chip key={c} size="small" variant="outlined" label={`ruleset: ${c}`} />
        ))}
      </Stack>

      {query.isLoading && <Typography variant="body2">Loading…</Typography>}

      {detail && detail.tenants.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Not materialised for any operator yet.
        </Typography>
      )}

      {detail && detail.tenants.length > 0 && (
        <Table size="small" sx={{ maxWidth: 780 }}>
          <TableHead>
            <TableRow>
              <TableCell>Operator</TableCell>
              <TableCell>Notification</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Managed</TableCell>
              <TableCell>Recipe</TableCell>
              <TableCell>Last refreshed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detail.tenants.map((t) => (
              <TableRow key={t.notification_id}>
                <TableCell>{t.tour_operator_code}</TableCell>
                <TableCell>
                  <Link component={RouterLink} to={`/notifications/${t.notification_id}`}>
                    #{t.notification_id}
                  </Link>
                </TableCell>
                <TableCell>{t.notification_enabled ? 'yes' : 'no'}</TableCell>
                <TableCell>
                  {MANAGED_LABELS.filter((m) => t.managed[m.key])
                    .map((m) => m.label.toLowerCase())
                    .join(', ') || 'none'}
                </TableCell>
                <TableCell>
                  {t.stale ? (
                    <Chip size="small" color="warning" label="older version" />
                  ) : (
                    'current'
                  )}
                </TableCell>
                <TableCell>
                  {formatRelativeTime(t.refreshed_ts ?? t.materialised_ts)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  );
}

export function CannedNotificationDetailDialog({
  row,
  onClose,
}: {
  row: CannedNotificationListItem;
  onClose: () => void;
}) {
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{row.name}</DialogTitle>
      <DialogContent>
        <CannedNotificationPanel row={row} />
      </DialogContent>
    </Dialog>
  );
}
